import { defineNuxtPlugin, useRuntimeConfig } from '#app'

import { brandedMessage } from '@baserow/modules/core/head'

/**
 * Exposes `$tBrand(key, values)` to every component.
 *
 * It is the drop-in replacement for `$t(key)` on the handful of brand-facing
 * strings that name the product ("New to Baserow?"). See `brandedMessage()` in
 * `core/head.js` for why those strings keep their stock value and gain a
 * `<key>Branded` sibling rather than having a `{brandName}` placeholder spliced
 * into the original value.
 *
 * Both inputs are app-wide — the name comes from the runtime config and the
 * translator from the i18n instance — so this is registered with
 * `nuxtApp.provide`, like the other app-wide helpers (`$hasFeature`,
 * `$hasPermission`, `$registry`). It deliberately does not read anything off
 * the calling component.
 *
 * The translator is read from `nuxtApp.$i18n`, which `@nuxtjs/i18n` sets to the
 * Composer (`i18n.global`). That object has `t`; a *component's* `$i18n` does
 * not, and reading `this.$i18n.t` here is what previously threw
 * "i18n.t is not a function" mid-render and turned into an HTTP 500 on any
 * server-rendered page reaching `$tBrand`.
 *
 * The reason the two differ is not obvious, so it is worth recording. This app
 * runs vue-i18n in Composition mode even though `i18n.config.ts` asks for
 * `legacy: true`: @nuxtjs/i18n's `bundle.compositionOnly` option defaults to
 * true and compiles to `__VUE_I18N_LEGACY_API__: "false"`
 * (node_modules/@nuxtjs/i18n/dist/module.mjs:47,1081), and vue-i18n gates its
 * Legacy API on that build-time constant, which makes the runtime `legacy`
 * flag inert. In Composition mode `injectGlobalFields` installs the translate
 * methods as their own `$t`/`$d`/`$n` globals and leaves `$i18n` carrying only
 * `locale`, `fallbackLocale` and `availableLocales`. So `$t` works in a
 * template while `$i18n.t` is undefined, two lines apart.
 *
 * It is called as a method so the Composer keeps its own internal state.
 */
export default defineNuxtPlugin((nuxtApp) => {
  const brandName = useRuntimeConfig().public.baserowBrandName

  nuxtApp.provide('tBrand', (key, values = {}) =>
    brandedMessage(
      (...args) => nuxtApp.$i18n.t(...args),
      brandName,
      key,
      values
    )
  )
})
